/*
 * @Author: YooHoeh
 * @LastEditors: YooHoeh
 *   ----- 热爱可抵岁月漫长 ------
 */
/**
 * @description Arco Design 插件：按需注册全站实际使用的主组件（子组件由各自 install 连带注册），
 * 替代原 app.use(ArcoVue) 全量注册以支持 tree-shaking，缩减入口 chunk 体积。
 * 组件清单经 _arco-verify 校验覆盖全站实际使用的 <a-xxx>/<AXxx> 标签；命令式
 * Modal 由业务文件自行 import；轻消息统一通过 BaseMessage 入口按端型分发。
 */
import Affix from '@arco-design/web-vue/es/affix'
import Alert from '@arco-design/web-vue/es/alert'
import Avatar from '@arco-design/web-vue/es/avatar'
import Badge from '@arco-design/web-vue/es/badge'
import Breadcrumb from '@arco-design/web-vue/es/breadcrumb'
import Button from '@arco-design/web-vue/es/button'
import Card from '@arco-design/web-vue/es/card'
import Cascader from '@arco-design/web-vue/es/cascader'
import Checkbox from '@arco-design/web-vue/es/checkbox'
import Collapse from '@arco-design/web-vue/es/collapse'
import DatePicker from '@arco-design/web-vue/es/date-picker'
import Descriptions from '@arco-design/web-vue/es/descriptions'
import Drawer from '@arco-design/web-vue/es/drawer'
import Dropdown from '@arco-design/web-vue/es/dropdown'
import Empty from '@arco-design/web-vue/es/empty'
import Form from '@arco-design/web-vue/es/form'
import Image from '@arco-design/web-vue/es/image'
import Input from '@arco-design/web-vue/es/input'
import InputNumber from '@arco-design/web-vue/es/input-number'
import List from '@arco-design/web-vue/es/list'
import Menu from '@arco-design/web-vue/es/menu'
import Modal from '@arco-design/web-vue/es/modal'
import Pagination from '@arco-design/web-vue/es/pagination'
import Popconfirm from '@arco-design/web-vue/es/popconfirm'
import Popover from '@arco-design/web-vue/es/popover'
import Radio from '@arco-design/web-vue/es/radio'
import Rate from '@arco-design/web-vue/es/rate'
import Result from '@arco-design/web-vue/es/result'
import Scrollbar from '@arco-design/web-vue/es/scrollbar'
import Select from '@arco-design/web-vue/es/select'
import Skeleton from '@arco-design/web-vue/es/skeleton'
import Space from '@arco-design/web-vue/es/space'
import Spin from '@arco-design/web-vue/es/spin'
import Statistic from '@arco-design/web-vue/es/statistic'
import Table from '@arco-design/web-vue/es/table'
import Tabs from '@arco-design/web-vue/es/tabs'
import Tag from '@arco-design/web-vue/es/tag'
import Textarea from '@arco-design/web-vue/es/textarea'
import Tooltip from '@arco-design/web-vue/es/tooltip'
import Trigger from '@arco-design/web-vue/es/trigger'
import {
  IconApps,
  IconClose,
  IconDown,
  IconFilter,
  IconLanguage,
  IconLeft,
  IconMenu,
  IconRight,
  IconSearch,
  IconSettings,
  IconUser,
} from '@arco-design/web-vue/es/icon'

// 按需注册的 Arco 主组件（子组件由各自 install 连带注册：如 Button→ButtonGroup、
// Form→FormItem、Table→TableColumn、Select→Option、Skeleton→SkeletonLine/Shape、
// Image→ImagePreview/Group、Statistic→Countdown 等）。
const arcoComponents = [
  Affix,
  Alert,
  Avatar,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Cascader,
  Checkbox,
  Collapse,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  List,
  Menu,
  Modal,
  Pagination,
  Popconfirm,
  Popover,
  Radio,
  Rate,
  Result,
  Scrollbar,
  Select,
  Skeleton,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Textarea,
  Tooltip,
  Trigger,
]

// 定制红色主题
const appTheme = {
  primary: '#F53F3F',
  primaryLight: '#FFECE8',
  primaryDark: '#CB272D',
  secondary: '#0747a6',
  success: '#36b37e',
  warning: '#ffab00',
  error: '#ff5630',
}

// 完整的红色主题调色板
const customTokens = {
  // 主色调 - 红色系
  colorPrimary: appTheme.primary,

  // 次要颜色
  colorSecondary: appTheme.secondary,

  // 功能色
  colorSuccess: appTheme.success,
  colorWarning: appTheme.warning,
  colorError: appTheme.error,

  // 文本色
  colorText: '#172b4d',
  colorText1: '#1d2129',
  colorText2: '#4e5969',
  colorText3: '#86909c',

  // 边框色
  colorBorder: '#e5e6eb',
  colorBorder1: '#f4f4f5',
  colorBorder2: '#e5e6eb',
  colorBorder3: '#dcdfe6',
  colorBorder4: '#c0c4cc',

  // 填充色
  colorFill1: '#f2f3f5',
  colorFill2: '#e5e6eb',
  colorFill3: '#c9cdd4',
  colorFill4: '#86909c',

  // 圆角
  borderRadiusSmall: '2px',
  borderRadiusMedium: '4px',
  borderRadiusLarge: '8px',
  borderRadiusHuge: '12px',

  // 字体
  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',

  // 阴影
  shadowSmall: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  shadowMedium: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
  shadowLarge: '0 4px 16px 0 rgba(0, 0, 0, 0.15)',
  shadowHuge: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
}

export default defineNuxtPlugin((nuxtApp) => {
  const app = nuxtApp.vueApp

  arcoComponents.forEach((component) => {
    app.use(component, { componentPrefix: 'A' })
  })

  app.component('IconClose', IconClose)
  app.component('IconFilter', IconFilter)
  app.component('IconMenu', IconMenu)
  app.component('IconSearch', IconSearch)
  app.component('IconUser', IconUser)
  app.component('IconLanguage', IconLanguage)
  app.component('IconDown', IconDown)
  app.component('IconApps', IconApps)
  app.component('IconSettings', IconSettings)
  app.component('IconLeft', IconLeft)
  app.component('IconRight', IconRight)

  const themeProvider = {
    setTheme: (theme = 'light') => {
      if (import.meta.client) {
        document.body.setAttribute('arco-theme', theme)
      }
    },
    getTheme: () => {
      if (import.meta.client) {
        return document.body.getAttribute('arco-theme') || 'light'
      }
      return 'light'
    },
  }

  app.provide('theme', themeProvider)

  return {
    provide: {
      arco: {
        version: '2.55.3',
        theme: appTheme,
        tokens: customTokens,
        setTheme: themeProvider.setTheme,
        getTheme: themeProvider.getTheme,
      },
    },
  }
})
